import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchTestSeriesAPI, deleteTestSeriesAPI, duplicateTestSeriesAPI } from "../../utils/api";
import { showConfirmation, showDeleteConfirmation, showSuccess, showError } from "../../utils/sweetAlert";
import { EmptyState, ErrorAlert, LoadingState, SearchField } from "../../components/ui";
import "./TestSeriesList.css";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "published", label: "Published" },
  { key: "draft", label: "Draft" },
  { key: "free", label: "Free" },
  { key: "premium", label: "Premium" },
];

// Values may be counts or arrays depending on the endpoint
const countOf = (value) => {
  if (Array.isArray(value)) return value.length;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const sectionsOf = (test) => test.sections_count ?? countOf(test.sections);
const questionsOf = (test) => test.questions_count ?? countOf(test.questions);
const attemptsOf = (test) => Number(test.attempts_count) || 0;

// Open / closed state of a test with a schedule, relative to now
const scheduleLabel = (test) => {
  const now = Date.now();
  const opens = test.starts_at ? new Date(test.starts_at) : null;
  const closes = test.ends_at ? new Date(test.ends_at) : null;
  const fmt = (d) => d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  if (opens && opens.getTime() > now) return { tone: "badge-info", text: `Opens ${fmt(opens)}` };
  if (closes && closes.getTime() < now) return { tone: "badge-neutral", text: "Closed" };
  if (closes) return { tone: "badge-warning", text: `Closes ${fmt(closes)}` };
  return null;
};

const isPublished = (test) => test.status === "published" || test.visibility === "published";
const hasSectionSwitching = (test) => Boolean(Number(test.section_toggle) || test.sectionToggle);
// Older data has no flag and is section-wise
const hasSectionalTiming = (test) => test.sectional_timing !== false;

const matchesFilter = (test, filter) => {
  switch (filter) {
    case "published":
      return isPublished(test);
    case "draft":
      return !isPublished(test);
    case "free":
    case "premium":
      return test.type === filter;
    default:
      return true;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
};

function TestSeriesList() {
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [testSeries, setTestSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const loadTestSeries = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchTestSeriesAPI();
      if (response?.success) {
        setTestSeries(response.data || []);
      } else if (response) {
        setError(response.message || "Failed to load test series.");
      }
    } catch (err) {
      console.error("Error fetching test series:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTestSeries();
  }, [loadTestSeries]);

  const query = searchTerm.trim().toLowerCase();
  const searched = testSeries.filter(
    (test) =>
      !query ||
      (test.title || test.name || "").toLowerCase().includes(query) ||
      (test.description || "").toLowerCase().includes(query)
  );
  const filteredTests = searched.filter((test) => matchesFilter(test, filter));

  const [duplicatingId, setDuplicatingId] = useState(null);

  const handleDuplicate = async (test, title) => {
    const result = await showConfirmation(
      `A draft copy of "${title}" will be created with all its sections, questions and images.`,
      "Duplicate this test?",
      "Duplicate"
    );
    if (!result.isConfirmed) return;

    try {
      setDuplicatingId(test.id);
      const response = await duplicateTestSeriesAPI(test.id);
      if (response?.success) {
        setTestSeries((prev) => [response.data, ...prev]);
        showSuccess(response.message, "Test duplicated");
      } else if (response) {
        showError(response.message || "Failed to duplicate the test series.");
      }
    } catch {
      showError("Failed to duplicate the test series. Please try again.");
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleDelete = async (id, title, attempts) => {
    if (attempts > 0) {
      // The server refuses this delete (it would remove students' results); say so up front
      showError(
        `${attempts} student attempt(s) exist for "${title}". Deleting it would delete their results, so set it to Draft instead (Edit → Visibility).`,
        "This test can't be deleted"
      );
      return;
    }

    const result = await showDeleteConfirmation("test series", title);
    if (!result.isConfirmed) return;

    try {
      setDeletingId(id);
      const response = await deleteTestSeriesAPI(id);
      if (response?.success) {
        setTestSeries((prev) => prev.filter((test) => test.id !== id));
        showSuccess(`"${title || "Test series"}" was deleted.`, "Test series deleted");
      } else {
        showError(response?.message || "Failed to delete the test series. Please try again.");
      }
    } catch (err) {
      console.error("Error deleting test series:", err);
      showError("Failed to delete the test series. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="test-series-list page">
      <div className="page-header">
        <div>
          <h2>Test Series</h2>
          <p>Create, organise and publish your test series</p>
        </div>
        <Link to="/admin/test-series/create" className="btn-primary">
          <i className="bi bi-plus-lg"></i> New test series
        </Link>
      </div>

      <ErrorAlert message={error} onRetry={loadTestSeries} />

      <div className="toolbar">
        <SearchField value={searchTerm} onChange={setSearchTerm} placeholder="Search test series…" />
        <div className="chip-group" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`chip ${filter === f.key ? "active" : ""}`}
              onClick={() => setFilter(f.key)}
              role="tab"
              aria-selected={filter === f.key}
            >
              {f.label}
              {!loading && (
                <span className="chip-count">{searched.filter((t) => matchesFilter(t, f.key)).length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading test series…" />
      ) : filteredTests.length === 0 ? (
        !error && (
          <EmptyState
            icon={testSeries.length === 0 ? "bi bi-journal-plus" : "bi bi-search"}
            title={testSeries.length === 0 ? "No test series yet" : "No matches"}
            message={
              testSeries.length === 0
                ? "Create your first test series to start adding sections and questions."
                : "Try a different search term or filter."
            }
            action={
              testSeries.length === 0 ? (
                <Link to="/admin/test-series/create" className="btn-primary">
                  <i className="bi bi-plus-lg"></i> Create test series
                </Link>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setFilter("all");
                    setSearchTerm("");
                  }}
                >
                  Clear filters
                </button>
              )
            }
          />
        )
      ) : (
        <div className="grid-cards">
          {filteredTests.map((test) => {
            const title = test.title || test.name || "Untitled test series";
            const published = isPublished(test);
            const switching = hasSectionSwitching(test);
            const timedSections = hasSectionalTiming(test);
            const totalMinutes = Number(test.total_duration_minutes) || 0;
            const isPremium = test.type === "premium";
            const schedule = scheduleLabel(test);

            return (
              <article key={test.id} className="ts-card card card-hover">
                <div className="ts-card-top">
                  <span className={`ts-card-icon ${isPremium ? "premium" : ""}`}>
                    <i className={isPremium ? "bi bi-gem" : "bi bi-journal-text"}></i>
                  </span>
                  <div className="ts-card-badges">
                    <span className={`badge badge-dot ${published ? "badge-success" : "badge-neutral"}`}>
                      {published ? "Published" : "Draft"}
                    </span>
                    <span className={`badge ${isPremium ? "badge-gold" : "badge-info"}`}>
                      {isPremium ? "Premium" : "Free"}
                    </span>
                    {schedule && <span className={`badge ${schedule.tone}`}>{schedule.text}</span>}
                  </div>
                </div>

                <h3 className="ts-card-title" title={title}>
                  {title}
                </h3>
                <p className="ts-card-desc">{test.description || "No description added."}</p>

                <div className="ts-card-meta">
                  {test.nested_sections ? (
                    <div title="Subjects, each split into sections">
                      <span>Subjects</span>
                      <strong>{(test.sections || []).filter((s) => s.parent_id == null).length}</strong>
                    </div>
                  ) : (
                    <div>
                      <span>Sections</span>
                      <strong>{sectionsOf(test)}</strong>
                    </div>
                  )}
                  <div>
                    <span>Questions</span>
                    <strong>{questionsOf(test)}</strong>
                  </div>
                  <div>
                    <span>Attempts</span>
                    <strong>{attemptsOf(test)}</strong>
                  </div>
                  <div>
                    <span>Created</span>
                    <strong>{formatDate(test.created_at || test.createdAt)}</strong>
                  </div>
                </div>

                {test.nested_sections && (
                  <div className="ts-card-switch" title="Each subject is split into sections that hold the questions">
                    <i className="bi bi-diagram-3"></i>
                    Subjects with sections ·{" "}
                    {(test.sections || []).filter((s) => s.parent_id != null).length} sections
                  </div>
                )}
                <div className="ts-card-switch" title="Whether students can move between sections during the test">
                  <i className={`bi ${switching ? "bi-toggle-on" : "bi-toggle-off"}`}></i>
                  Section switching {switching ? "on" : "off"}
                </div>
                <div
                  className="ts-card-switch"
                  title={
                    timedSections
                      ? "Every section has its own timer"
                      : "One timer for the whole test; section durations are not used"
                  }
                >
                  <i className="bi bi-stopwatch"></i>
                  {`${timedSections ? "Timed per section" : "One timer"}${totalMinutes > 0 ? ` · ${totalMinutes} min` : ""}`}
                </div>

                <div className="ts-card-actions">
                  <Link to={`/admin/test-series/${test.id}/sections`} className="btn-primary btn-sm">
                    <i className="bi bi-layers"></i> Manage sections
                  </Link>
                  <Link
                    to={`/admin/test-series/${test.id}/edit`}
                    className="btn-secondary btn-icon btn-sm"
                    title="Edit details"
                    aria-label="Edit details"
                  >
                    <i className="bi bi-pencil"></i>
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDuplicate(test, title)}
                    className="btn-secondary btn-icon btn-sm"
                    title="Duplicate"
                    aria-label="Duplicate"
                    disabled={duplicatingId === test.id}
                  >
                    {duplicatingId === test.id ? <span className="btn-spinner"></span> : <i className="bi bi-copy"></i>}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(test.id, title, attemptsOf(test))}
                    className="btn-danger btn-icon btn-sm"
                    title="Delete"
                    aria-label="Delete"
                    disabled={deletingId === test.id}
                  >
                    {deletingId === test.id ? <span className="btn-spinner"></span> : <i className="bi bi-trash3"></i>}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TestSeriesList;
